<template>
  <div v-rect-loading="loading" class="step-editor">
    <div class="header">
      <div class="title">{{ $t(`${i18nPrefix}.title.step.${isEditMode ? 'edit' : 'add'}`) }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        class="form-container"
        ref="formEditor"
        :labelWidth="labelWidth"
        :model="step"
        :formLines="formLines"
      >
        <template #icon="{ lineName }">
          <div class="step-icon-container">
            <div class="icon-container">
              <i :class="step[lineName]"></i>
            </div>
            <el-button @click="openIconSelector">{{ $t('common.btn.select') }}</el-button>
          </div>
        </template>
        <template #questions="{ lineName }">
          <div class="questions-list-container">
            <div class="top-btn-group">
              <el-button class="add-btn" @click="openQuestionSelector">{{ $t('common.btn.add') }}</el-button>
              <el-button class="delete-btn" @click="removeStepQuestion">{{ $t('common.btn.delete') }}</el-button>
            </div>
            <TableList
              noHead
              noAction
              ref="stepQuestions"
              :listData="step[lineName]"
              :tableColumns="formQuestionTableColumns"
              @selection-change="handleFormQuestionSelectionChange"
            >
              <template #draggable>
                <div class="drag-btn">
                  <i class="iconfont iconMore draggable-handle"></i>
                </div>
              </template>
              <template #isRequiredQuestion="{ data, row }">
                <el-switch :value="data" @change="handleQuestionRequiredChange($event, row)"></el-switch>
              </template>
            </TableList>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
    <DialogWrapper
      class="icon-selector"
      :show.sync="showIconSelector"
      :titleI18nKey="`${i18nPrefix}.title.iconSelector`"
      @close="closeIconSelector"
    >
      <el-row class="icon-list">
        <el-col
          v-for="(item, index) in iconList"
          class="icon-item"
          :key="index"
          :class="selectIcon === item && 'is-selected'"
          @click.native="handleIconClick(item)"
        >
          <i :class="item"></i>
        </el-col>
      </el-row>
    </DialogWrapper>
    <DialogWrapper
      class="question-selector"
      :loading="questionSelectorLoading"
      :show.sync="showQuestionSelector"
      :titleI18nKey="`${i18nPrefix}.title.questionSelector`"
      @close="closeQuestionSelector"
      @save="handleQuestionSelect"
    >
      <div class="filter-container">
        <el-select
          v-model="questionSearchCondition.type"
          class="question-type"
          clearable
          :placeholder="$t(`${i18nPrefix}.placeholder.questionType`)"
          @change="fetchQuestionListData"
        >
          <el-option
            v-for="(item, key) in questionType"
            :key="key"
            :label="$t(item.i18nKey)"
            :value="item.value"
          ></el-option>
        </el-select>
        <el-input
          v-model="questionSearchCondition.keyword"
          class="keyword"
          suffix-icon="el-icon-search"
          :placeholder="$t(`${i18nPrefix}.placeholder.searchKeyword.question`)"
          @change="fetchQuestionListData"
          @keypress.enter.native="fetchQuestionListData"
        ></el-input>
      </div>
      <TableList
        noHead
        noAction
        tableHeight="500px"
        :listData="questionList"
        :tableColumns="questionTableColumns"
        @selection-change="handleQuestionSelectionChange"
      >
      </TableList>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { QUESTIONNAIRE_QUESTION_TYPE, QUESTION_UNSUPPORT_TYPE } from '@/constant/questionnaire'
import { QUESTIONNAIRE_ICON } from '@/constant/fontIconNames'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { isEn } from '@/i18n'

export default {
  components: {
    WrappedFrom,
    DialogWrapper,
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.postApplicant',
      iconList: QUESTIONNAIRE_ICON,
      questionType: QUESTIONNAIRE_QUESTION_TYPE,
      unSupportType: QUESTION_UNSUPPORT_TYPE,
      step: {
        icon: 'el-icon-menu',
        questions: []
      },
      NPSType: 'NPS',
      selectQuestionIds: [],
      questionsVid: 1,
      showIconSelector: false,
      selectIcon: '',
      showQuestionSelector: false,
      allQuestion: [],
      questionList: [],
      questionSearchCondition: {
        type: null,
        keywords: null
      },
      questionSelectorLoading: false,
      questionSelectionIds: [],
      formQuestionSelectionIds: [],
      questionLoaded: false
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    formLines () {
      const titleRules = { required: true, message: this.$t(`${this.i18nPrefix}.message.stepNameRequired`) }
      const lines = {
        title_cn: {
          label: this.$t(`${this.i18nPrefix}.label.name.cn`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
          rules: titleRules
        },
        title_tc: {
          label: this.$t(`${this.i18nPrefix}.label.name.tc`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
          rules: titleRules
        },
        title_en: {
          label: this.$t(`${this.i18nPrefix}.label.name.en`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
          rules: titleRules
        },
        icon: {
          label: this.$t(`${this.i18nPrefix}.label.stepIcon`),
          contentSlot: 'icon',
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.stepIconRequired`) }
        },
        questions: {
          label: this.$t(`${this.i18nPrefix}.label.question`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.question`),
          contentSlot: 'questions',
          rules: { required: true, validator: this.validateQuestions, trigger: 'change' }
        }
      }
      return lines
    },
    questionTableColumns () {
      return [
        {
          width: '50px',
          cellClass: 'table-selection',
          columnType: 'selection'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          dataKey: 'type',
          formatter: (data) => {
            return this.$t(this.questionType[data].i18nKey)
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.cn`),
          dataKey: 'content_cn',
          width: '110px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.tc`),
          dataKey: 'content_tc',
          width: '110px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name.en`),
          dataKey: 'content_en',
          width: '100px'
        }
      ]
    },
    formQuestionTableColumns () {
      const formQuestionTableColumns = _.cloneDeep(this.questionTableColumns)
      formQuestionTableColumns.splice(1, 0,
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: '50px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.isRequiredQuestion`),
          slotName: 'isRequiredQuestion',
          dataKey: 'is_required'
        }
      )
      return formQuestionTableColumns
    },
    form () {
      return this.$refs.formEditor
    },
    labelWidth () {
      if (isEn()) {
        return '206px'
      } else {
        return '120px'
      }
    }
  },
  created () {
    if (this.isEditMode) {
      this.getStepData()
    }
  },
  mounted () {

  },
  methods: {
    ...mapActions('postApplicantManagement', ['getStepById', 'createStep', 'updateStep', 'searchQuestion']),
    async getStepData () {
      this.loading = true
      try {
        const stepId = this.$route.params.stepId
        const resp = await this.getStepById(stepId)
        this.step = resp
        this.selectQuestionIds = _.map(this.step.questions, 'id')
      } finally {
        this.loading = false
      }
    },
    async openQuestionSelector () {
      this.showQuestionSelector = true
      this.questionSelectorLoading = true
      try {
        if (!this.questionLoaded) {
          const resp = await this.searchQuestion({ status: 1 })
          this.questionLoaded = true
          if (resp) {
            this.allQuestion = resp.question
          }
        }
        this.fetchQuestionListData()
      } finally {
        this.questionSelectorLoading = false
      }
    },
    fetchQuestionListData () {
      this.questionSelectorLoading = true
      const type = this.questionSearchCondition.type
      const keyword = this.questionSearchCondition.keyword
      this.questionList = _.filter(this.allQuestion, item => {
        let isType = true
        let isKeywords = true
        const isSelected = _.includes(this.selectQuestionIds, item.id)
        if (type) {
          isType = item.type === type
        }
        if (keyword) {
          const searchText = [item.content_cn, item.content_tc, item.content_en]
          isKeywords = _.some(searchText, text => _.includes(text.toLowerCase(), keyword.toLowerCase()))
        }
        return isType && isKeywords && (!isSelected)
      })
      this.questionSelectorLoading = false
    },
    closeQuestionSelector () {
      this.showQuestionSelector = false
    },
    openIconSelector () {
      this.selectIcon = this.step.icon
      this.showIconSelector = true
    },
    closeIconSelector () {
      this.showIconSelector = false
    },
    handleIconClick (item) {
      this.selectIcon = item
      this.step.icon = this.selectIcon
      this.showIconSelector = false
    },
    handleIconSelect () {
      this.step.icon = this.selectIcon
      this.showIconSelector = false
    },
    handleQuestionSelectionChange (selection) {
      this.questionSelectionIds = _.map(selection, 'id')
    },
    handleFormQuestionSelectionChange (selection) {
      this.formQuestionSelectionIds = _.map(selection, 'id')
    },
    handleQuestionSelect () {
      const selectQuestionList = _.chain(this.allQuestion)
        .filter(item => _.includes(this.questionSelectionIds, item.id))
        .map(item => {
          this.selectQuestionIds.push(item.id)
          return {
            ...item,
            is_required: true
          }
        }).value()
      this.step.questions.push(...selectQuestionList)
      this.form.clearValidate('questions')
      this.showQuestionSelector = false
    },
    removeStepQuestion () {
      this.step.questions = _.filter(this.step.questions, item => !this.formQuestionSelectionIds.includes(item.id))
      this.selectQuestionIds = _.map(this.step.questions, 'id')
      this.formQuestionSelectionIds = []
    },
    handleQuestionRequiredChange (status, question) {
      question.is_required = status
    },
    validateQuestions (rule, value, callback) {
      if (value && value.length > 0) {
        callback()
      } else {
        callback(this.$t(`${this.i18nPrefix}.message.stepQuestionsRequired`))
      }
    },
    handleCancel () {
      this.$router.go(-1)
    },
    async handleSave () {
      let result = false
      result = await this.form.submit()
      if (!result) {
        return
      }
      _.forEach(this.step.questions, (item, index) => {
        item.order = index + 1
      })
      this.loading = true
      try {
        let saveFunction = null
        if (this.isEditMode) {
          saveFunction = this.updateStep
        } else {
          saveFunction = this.createStep
        }
        const resp = await saveFunction(this.step)
        if (resp) {
          this.$router.push({ path: '/v-admin/post-applicant-management/step/list' })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.step-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 812px;
    margin: 30px auto 0;

    .form-container {
      ::v-deep {
        .el-form-item__label-wrap {
          .el-form-item__label {
            .label-inner {
              margin-top: 0;
              line-height: inherit;
            }
          }
        }
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .step-icon-container {
    display: flex;

    .icon-container {
      padding: 5px 16px;
      border: 1px solid #DCDFE6;
      font-size: 32px;
      line-height: 32px;
      color: #3595cc;
    }

    .el-button {
      margin-left: 10px;
    }
  }

  .questions-list-container {
    .top-btn-group {
      margin-bottom: 20px;
    }

    .el-table {
      .draggable-handle {
        font-size: 16px;
        cursor: grab;
      }
    }
  }

  .icon-selector {
    .icon-list {
      margin-left: 1px;

      .icon-item {
        width: 100px;
        height: 100px;
        line-height: 100px;
        text-align: center;
        border: 1px solid #eee;
        position: relative;
        margin: 0px -1px -1px 0px;

        &::after {
          display: none;
          width: 30px;
          height: 30px;
          line-height: 30px;
          position: absolute;
          right: 0;
          top: 0;
          font-family: element-icons;
          text-transform: none;
          font-weight: 400;
          font-size: 24px;
          color: #5cb6ff;
          content: "\e6da";
        }

        &.is-selected {
          &::after {
            display: block;
          }
        }

        i {
          font-size: 32px;
          color: #606266;
        }
      }
    }
  }

  .question-selector {
    .filter-container {
      display: flex;
      margin-bottom: 20px;

      .el-select {
        width: 200px;
        margin-right: 20px;
      }
    }

    .el-table {
      ::v-deep {
        td:first-child .cell {
          padding-left: 15px;
        }
      }
    }
  }
}
</style>
